"use client";

import NProgress from "nprogress";
import "nprogress/nprogress.css";

export type ProgressOptions = {
  startDelay?: number; // atraso anti-flicker para iniciar
  softFinish?: number; // atraso para concluir (fallback)
  trickleSpeed?: number; // velocidade de trickle (incremento)
  showSpinner?: boolean; // exibe spinner
};

export interface ProgressService {
  intentStart(): void; // intenção de navegação (clique/popstate)
  cancelIntent(): void; // cancela start pendente
  softFinish(): void; // conclui após pequeno atraso (fallback)
  forceDone(): void; // conclui imediatamente
  // eslint-disable-next-line no-unused-vars
  configure(options: Omit<ProgressOptions, "startDelay" | "softFinish">): void;
}

export const PROGRESS_BAR_ANTI_FLICKER_DELAY_MS = 300; // atraso anti-flicker para iniciar
export const PROGRESS_BAR_SOFT_FINISH_FALLBACK_MS = 400; // atraso para concluir (fallback)
export const PROGRESS_BAR_TRICKLE_SPEED_MS = 150; // velocidade de trickle (incremento)

class NProgressService implements ProgressService {
  private startTimer: number | null = null;
  private finishTimer: number | null = null;

  private startDelay: number;
  private softFinishDelay: number;

  constructor(opts?: ProgressOptions) {
    const {
      startDelay = PROGRESS_BAR_ANTI_FLICKER_DELAY_MS,
      softFinish = PROGRESS_BAR_SOFT_FINISH_FALLBACK_MS,
      trickleSpeed = PROGRESS_BAR_TRICKLE_SPEED_MS,
      showSpinner = false
    } = opts ?? {};
    this.startDelay = startDelay;
    this.softFinishDelay = softFinish;
    NProgress.configure({ trickleSpeed, showSpinner });
  }

  configure(options: Omit<ProgressOptions, "startDelay" | "softFinish">) {
    NProgress.configure(options);
  }

  intentStart() {
    // agenda o início (anti-flicker)
    if (this.startTimer) clearTimeout(this.startTimer);
    this.startTimer = window.setTimeout(() => {
      if (NProgress.status == null) NProgress.start();
    }, this.startDelay);
  }

  cancelIntent() {
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
  }

  softFinish() {
    // cancela início pendente e agenda conclusão suave (fallback)
    this.cancelIntent();
    if (this.finishTimer) clearTimeout(this.finishTimer);
    this.finishTimer = window.setTimeout(() => {
      if (NProgress.status != null) NProgress.done();
    }, this.softFinishDelay);
  }

  forceDone() {
    // conclui agora (útil quando se tem certeza que terminou)
    this.cancelIntent();
    if (this.finishTimer) {
      clearTimeout(this.finishTimer);
      this.finishTimer = null;
    }
    if (NProgress.status != null) NProgress.done();
  }
}

// instância única para o app
export const progress: ProgressService = new NProgressService({
  startDelay: PROGRESS_BAR_ANTI_FLICKER_DELAY_MS,
  softFinish: PROGRESS_BAR_SOFT_FINISH_FALLBACK_MS,
  trickleSpeed: PROGRESS_BAR_TRICKLE_SPEED_MS,
  showSpinner: false
});
